import React from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Authorize from '../../../components/AuthComponent/Authorize';
import ButtonBar from '../../../components/Fields/ButtonBar';
import Form from '../../../components/Fields/Form';
import DateControl from '../../../components/Fields/DateControl';
import Field from '../../../components/Fields/Field';
import FieldLabel from '../../../components/Fields/FieldLabel';
import SelectListField from '../../../components/Fields/SelectListField';
import TextboxControl from '../../../components/Fields/TextboxControl';
import TextboxField from '../../../components/Fields/TextboxField';
import { FieldConfig, FormConfig } from '../../../components/Fields/Types';
import useSelectListOptions from '../../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../../components/Hooks/ValidationHook/useValidation';
import AddressModel from '../../../models/AddressModel';
import { DataResult } from '../../../types/Result';
import { ApplicationFunctions } from '../../../contexts/authContext';
import SaveButton from '../../../components/Buttons/SaveButton';
import ActionMenu from '../../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../../components/ActionMenu/ActionMenuItem';

interface IProps {
    formData: AddressModel
    handleSubmit: () => void
    handleFieldChanged: (name: string, value: string) => void
    serverErrors: DataResult<AddressModel>
}

const AddressControl: React.FC<IProps> = ({ formData, handleSubmit, handleFieldChanged, serverErrors } ) => {
    const addressTypes = useSelectListOptions('api/v1/selectlist/AddressTypes', '-- Select address type');
    const countries = useSelectListOptions('api/v1/selectlist/Countries', '-- Select country');

    let fields: FieldConfig[] = [
        { name: 'addressTypeId', label: 'Address type' },
        { name: 'addressLine1', label: 'Addressline 1' },
        { name: 'addressLine2', label: 'Addressline 2' },
        { name: 'addressLine3', label: 'Addressline 3' },
        { name: 'zipCode', label: 'Zipcode / City' },
        { name: 'city', label: 'City' },
        { name: 'countryId', label: 'Country' },
        { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
        { name: 'endDate', label: 'Active until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
    ];

    const formConfig: FormConfig = {
        fields: fields,
        handleSubmit: handleSubmit,
        handleFieldChanged: handleFieldChanged,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    return (
        <Form title='Address' footer={(<>
            <ButtonBar offset={2}>
                <Authorize applicationFunction={ApplicationFunctions.UpdateAddress}>
                    <SaveButton {...getSubmitButtonProps()} />
                </Authorize>
            </ButtonBar>
        </>)}>
            <Container fluid>
                <Row>
                    <Col md='12'>
                        {addressTypes && addressTypes.length > 1 && <SelectListField {...getFieldProps('addressTypeId')} options={addressTypes} />}
                        <TextboxField {...getFieldProps('addressLine1')} />
                        <TextboxField {...getFieldProps('addressLine2')} />
                        <TextboxField {...getFieldProps('addressLine3')} />
                        <Field {...getLabelProps('zipCode')}>
                            <Col md='4' style={{ padding: 0 }}>
                                <TextboxControl {...getControlProps('zipCode')} />
                            </Col>
                            <Col md='8' style={{ paddingRight: 0 }}>
                                <TextboxControl {...getControlProps('city')} />
                            </Col>
                        </Field>
                        <SelectListField {...getFieldProps('countryId')} options={countries} />
                        <Field {...getLabelProps('beginDate')}>
                            <DateControl {...getControlProps('beginDate')} />
                            <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                            <DateControl {...getControlProps('endDate')} />
                        </Field>
                    </Col>
                </Row>
            </Container>
        </Form>
    );
}
export default AddressControl;
